import type { Appointment, AppointmentPayload } from "@/types/deals.types";
import type { AppointmentValues } from "@/schemas/deals";

export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const pad = (n: number) => String(n).padStart(2, "0");

const zonedParts = (iso: string, zone: string) => Object.fromEntries(
  new Intl.DateTimeFormat("en-US", {
    timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(iso)).map((p) => [p.type, p.value]),
);

export const isoToLocalParts = (iso: string, zone = browserTimeZone()): { date: string; time: string } => {
  const p = zonedParts(iso, zone);
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
};

/** A wall-clock time in an IANA zone, with its offset. Reject nonexistent DST times. */
export const localToIso = (date: string, time: string, zone = browserTimeZone()): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
    throw new Error("Pick a valid date and time.");
  }
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const wall = Date.UTC(y, m - 1, d, hh, mm);
  const day = new Date(wall);
  if (day.getUTCFullYear() !== y || day.getUTCMonth() !== m - 1 || day.getUTCDate() !== d) {
    throw new Error("Pick a valid date.");
  }
  const offsets = new Set<number>();
  for (const delta of [-86400000, 0, 86400000]) {
    const probe = wall + delta;
    const p = zonedParts(new Date(probe).toISOString(), zone);
    offsets.add((Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - probe) / 60000);
  }
  const candidates = [...offsets].map((offset) => ({ offset, epoch: wall - offset * 60000 }))
    .filter(({ epoch }) => {
      const parts = isoToLocalParts(new Date(epoch).toISOString(), zone);
      return parts.date === date && parts.time === time;
    }).sort((a, b) => a.epoch - b.epoch);
  if (!candidates.length) throw new Error("That time doesn't exist in this time zone. Choose another time.");
  const offset = candidates[0].offset;
  const abs = Math.abs(offset);
  return `${date}T${time}:00${offset >= 0 ? "+" : "-"}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
};

export const addMinutes = (time: string, minutes: number): string => {
  const [hh, mm] = time.split(":").map(Number);
  const total = (((hh * 60 + mm + minutes) % 1440) + 1440) % 1440;
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
};

export const appointmentPayload = (v: AppointmentValues, existing?: Appointment | null): AppointmentPayload => {
  const instant = (field: "starts_at" | "ends_at", time: string) => {
    if (existing && existing.timezone === v.timezone) {
      const original = isoToLocalParts(existing[field], existing.timezone);
      if (original.date === v.date && original.time === time) return existing[field];
    }
    return localToIso(v.date, time, v.timezone);
  };
  return {
    ...(existing ? { expected_sequence: existing.sequence } : { kind: v.kind }),
    title: v.title, location: v.location, notes: v.notes, timezone: v.timezone,
    starts_at: instant("starts_at", v.start_time), ends_at: instant("ends_at", v.end_time),
    attendee_user_ids: v.attendee_user_ids,
    guest_emails: [...new Set(v.guest_emails.split(/[\s,;]+/).filter(Boolean).map((e) => e.toLowerCase()))],
  };
};

/** Mirrors the email's range, always using the appointment's stored zone. */
export const formatAppointmentWhen = (a: Pick<Appointment, "starts_at" | "ends_at" | "timezone">): string => {
  const parts = (iso: string) => Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    timeZone: a.timezone || "UTC", year: "numeric", weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short",
  }).formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  const s = parts(a.starts_at), e = parts(a.ends_at);
  const day = `${s.weekday}, ${s.month} ${s.day}`;
  const clock = (p: Record<string, string>) => `${p.hour}:${p.minute}`;
  if (s.day !== e.day || s.month !== e.month || s.year !== e.year) {
    return `${day} · ${clock(s)} ${s.dayPeriod} – ${e.weekday}, ${e.month} ${e.day} · ${clock(e)} ${e.dayPeriod} ${e.timeZoneName}`;
  }
  if (s.timeZoneName !== e.timeZoneName) {
    return `${day} · ${clock(s)} ${s.dayPeriod} ${s.timeZoneName} – ${clock(e)} ${e.dayPeriod} ${e.timeZoneName}`;
  }
  const start = s.dayPeriod === e.dayPeriod ? clock(s) : `${clock(s)} ${s.dayPeriod}`;
  return `${day} · ${start} – ${clock(e)} ${e.dayPeriod} ${e.timeZoneName}`;
};
